import { useEffect, useState } from 'react';
import { FC } from 'react';
import { Box, CircularProgress, TextField, FormHelperText, Grid, styled, DialogContent, Typography } from '@mui/material';
import { Button, NumericFormatField } from '@gisce/oficina-virtual-components';
import { useTranslation } from 'react-i18next';
import { z } from "zod";
import { useContracts } from '@/components/Contracts/hooks/useContracts';
import { ContractDisplayTypes } from '@/components/Contracts/types/contracts';
import Banner from '@/components/Banner/Banner';
import SignatureProcess from '@/components/SignatureProcess';
import ErrorMessage from '@/components/Error/ErrorMessage';
import usePower from '@/components/Contracts/hooks/usePower';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { MuiTelInput, matchIsValidTel } from "mui-tel-input";
import { StepElecContractFormData } from '@/components/Contractation/types/stepsFormData';
import { zodResolver } from '@hookform/resolvers/zod';
import { PowerData, PowerKeys } from '@/components/Contracts/types/power';
import { LoadingAnimation } from '@/components/LoadingAnimation';
import { useSettingsContext } from '@/components/Settings/hooks/useSettingsContext';

interface ChangePowerProps {
  type?: ContractDisplayTypes;
  onClose: () => void;
}

const NoFlagMuiTelInputStyled = styled(MuiTelInput)`
  .MuiTelInput-IconButton {
    display: none;
  }
`;

const ChangePower: FC<ChangePowerProps> = ({
  type = "electricity",
  onClose
}) => {
  const { currentContract } = useContracts(type);
  const Settings = useSettingsContext();

  const { t } = useTranslation();

  const { status, waitingForSignature, handleSendPower } = usePower();

  const handleClose = () => {
    onClose();
  };

  const schema = z.object({
    power: z.number({
      invalid_type_error: t('common:text.required_field')
    }).min(1, {message: t('common:text.required_field')}),
    power2: z.number({
      invalid_type_error: t('common:text.required_field')
    }).min(1, {message: t('common:text.required_field')}),
    power3: z.number({
      invalid_type_error: t('common:text.required_field')
    }).optional(),
    power4: z.number({
      invalid_type_error: t('common:text.required_field')
    }).optional(),
    power5: z.number({
      invalid_type_error: t('common:text.required_field')
    }).optional(),
    power6: z.number({
      invalid_type_error: t('common:text.required_field')
    }).optional(),
    phone: z.string()
      .min(4, {message: t('common:text.required_field')})
      .refine(
        (tel) => {
          return !tel || matchIsValidTel(tel);
        },
        { message: t('common:text.lead_contact_phone_not_valid') }
      )
  }).superRefine((data, ctx) => {
    if (data.power <= 15 && data.power2 <= 15) {
      const p1IsEqual = currentContract?.contracted_power_period[0].power === data.power;
      const p2IsEqual = currentContract?.contracted_power_period[1].power === data.power2;

      if (p1IsEqual && p2IsEqual) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('common:text.power_modification_same'),
          path: ["power"],
        });
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('common:text.power_modification_same'),
          path: ["power2"],
        });
      }
    }
    else {
      const powersCheck: (keyof Pick<
        StepElecContractFormData, 
        "power" | "power2" | "power3" | "power4" | "power5" | "power6"
      >)[] = ["power3", "power4", "power5", "power6"];

      powersCheck.map((powerNum, idx) => {
        if (!(powerNum in data) || (powerNum in data && ( typeof data[powerNum] !== "number" || data[powerNum] <= 0))) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('common:text.required_field'),
            path: [powerNum],
          });
        }
        else{
          if (currentContract?.contracted_power_period[idx].power === data[powerNum] ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('common:text.power_modification_same'),
              path: [powerNum],
            });
          }
        }
      })

      for (let i = 0; i < powersCheck.length - 1; i++) {
        const current = powersCheck[i];
        const next = powersCheck[i + 1];
  
        if (data[current] !== undefined && data[next] !== undefined && data[next] < data[current]) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(`common:text.field_must_be_greater_than_previous_field`),
            path: [next],
          });
        }
      }
    }
  });

  const methods = useForm<PowerData>({
    resolver: zodResolver(schema),
    defaultValues: {
      power: currentContract?.contracted_power_period[0]?.power,
      power2: currentContract?.contracted_power_period[1]?.power,
      power3: currentContract?.contracted_power_period[2]?.power,
      power4: currentContract?.contracted_power_period[3]?.power,
      power5: currentContract?.contracted_power_period[4]?.power,
      power6: currentContract?.contracted_power_period[5]?.power,
      phone: typeof currentContract?.notification_address?.mobile === 'string'
        ? (
            (currentContract.notification_address.mobile as string).startsWith('+')
              ? currentContract.notification_address.mobile
              : '+34' + currentContract.notification_address.mobile
          )
        : '',
    }
  });

  const {
    control,
    handleSubmit,
    getValues,
    watch,
    trigger,
    formState: { errors, isSubmitting }
  } = methods;

  const watchedPower1 = watch("power");
  const watchedPower2 = watch("power2");

  useEffect(() => {
      trigger("power");
      trigger("power2");
  }, [watchedPower1, watchedPower2]);

  const onChangePowerSubmit = (formValues: PowerData) => {
    handleSendPower(formValues);
  }

  const renderSent = () => (
    <div>
      <SignatureProcess />
      {status === "error" && <ErrorMessage><span>{t('common:text.power_same_error_modification_close')}</span></ErrorMessage>}
      <Button
        variant={'contained'}
        style={{ marginTop: '12px' }}
        color={'primary'}
        onClick={handleClose}
      >
        {t('common:text.power_modification_close')}
      </Button>
    </div>
  );

  const sixPowers = (watchedPower1 > 15 || watchedPower2 > 15);

  const powerField = (
    name: PowerKeys,
  ) => (<>
    <Controller
      name={name}
      control={control}
      render={({ field }) => <TextField
        sx={{width: '100%'}}
        label={t('common:text.contractation_' + name)}
        InputProps={{
          inputComponent: NumericFormatField,
          inputProps: {
            suffix:" kW",
            allowNegative: false,
            allowedDecimalSeparators: [","],
            decimalScale: 3,
            decimalSeparator: ",",
            thousandSeparator: ".",
          }
        }}
        helperText={errors[name]?.message}
        variant="standard"
        error={!!errors[name]}
        {...field}
        value={field.value ?? ''}
        onChange={(e) => {
          field.onChange(Number(e.target.value));
          trigger(name);
        }}
      />}
    />
  </>);

  const domesticPowerNames: Parameters<typeof powerField>[0][] = [
    "power", "power2"
  ];

  const domesticPowerFields = <>
    <Grid container item sm={12} spacing={2}>
      {domesticPowerNames.map((powerName) => (
        <Grid item xs={12} sm={6} key={powerName}>
          {powerField(powerName)}
        </Grid>
      ))}
    </Grid>
  </>

  const industrialPowerNames: Parameters<typeof powerField>[0][] = [
    "power3", "power4", "power5", "power6"
  ];

  const industrialPowerFields = <>
    <Grid container item md={12} spacing={2}>
      {industrialPowerNames.map((powerName) => (
          <Grid item xs={12} sm={6} key={powerName}>
            {powerField(powerName)}
          </Grid>
        )
      )}
    </Grid>
  </>

  const renderForm = () => {
    const values = getValues();

    let hasLowerPot = false;
    let hasHigherPot = false;

    if (currentContract?.contracted_power_period) {
      Object.entries(values).forEach(([key, powerValue]) => {
        if (!key.startsWith('power')) return;

        const periodIndex = key === 'power' ? '1' : key.replace('power', '');

        const keySuffix = 'P' + periodIndex;

        const periodObj = currentContract?.contracted_power_period.find(p => p.period === keySuffix);

        if (periodObj && typeof powerValue === 'number' && !isNaN(powerValue)) {
          if (powerValue > periodObj.power) {
            hasHigherPot = true;
          } else if (powerValue < periodObj.power) {
            hasLowerPot = true;
          }
        }
      });
    }

    return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onChangePowerSubmit)}>
        <DialogContent>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={hasHigherPot || hasLowerPot ? 6 : 12}>
              <Box display="flex" flexDirection="column" justifyContent="flex-start" height="100%" gap={2}>
                {domesticPowerFields}
                {sixPowers && industrialPowerFields}
                <Controller
                  name="phone"
                  control={control}
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <NoFlagMuiTelInputStyled
                        {...field}
                        label={t('common:text.address_phone')}
                        style={{ width: "100%" }}
                        error={!!(error)}
                        disableDropdown={true}
                        defaultCountry="ES"
                        helperText={error?.message}
                        fullWidth={true}
                      />
                    );
                  }}
                />
                <FormHelperText error={true}>
                  {!!errors.phone}
                </FormHelperText>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" flexDirection="column" justifyContent="flex-start" height="100%">
                {hasHigherPot ? (
                  <div>
                    <Typography variant="h6">{t('common:text.power_modification_associated_costs_title')}</Typography>
                    <ul>
                      <li>{t('common:text.power_modification_paste_rights')}</li>
                      <li>{t('common:text.power_modification_acces_rights')}</li>
                      <li>{t('common:text.power_modification_extension_rights')}</li>
                    </ul>
                  </div>
                ) : hasLowerPot ? (
                  <div>
                    <Typography variant="h6">{t('common:text.power_modification_associated_costs_title')}</Typography>
                    <ul>
                      <li>{t('common:text.power_modification_paste_rights')}</li>
                    </ul>
                  </div>
                ) : null}
              </Box>
                <Banner code='canvi_potencia_ov' sx={{ height: '100%', width: '100%', minHeight: '4rem' }}/>
            </Grid>
          </Grid>

          <div style={{ marginTop: '12px' }}>
            <Button
              style={{ marginRight: '12px' }}
              variant={'outlined'}
              color={'primary'}
              onClick={handleClose}
              disabled={status === "submitting"}
            >
              {t('common:text.power_modification_close')}
            </Button>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              disabled={isSubmitting || status === "submitting"}
            >
              {isSubmitting || status === "submitting" ? <CircularProgress size={25} /> : null}
              {t('common:text.power_modification_activate')}
            </Button>
          </div>
        </DialogContent>
      </form>
    </FormProvider>
    )
  };

  return (
    <Grid
      container
      direction="row"
      alignItems="stretch"
    >
      <Grid item xs={12} style={{ padding: 12 }}>
        <Typography variant="h5">{t('common:text.power_modification_title')}</Typography>
        {status === 'fetching' && <LoadingAnimation />}
        {(status === "uninitialized" || status === "submitting") && renderForm()}
        {(status === 'success' || status === 'error') && renderSent()}
      </Grid>
    </Grid>
  );
};

export default ChangePower;
